import { Button, CircularProgress, ButtonProps } from '@mui/material';
import { ReactNode } from 'react';

export type AppButtonVariant = 'primary' | 'secondary' | 'outlined' | 'text';
export type AppButtonSize = 'small' | 'medium' | 'large';

interface AppButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  /** Button variant style */
  variant?: AppButtonVariant;
  /** Button size */
  size?: AppButtonSize;
  /** Show loading spinner */
  loading?: boolean;
  /** Icon to display at the start */
  startIcon?: ReactNode;
  /** Icon to display at the end */
  endIcon?: ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Button children */
  children: ReactNode;
}

/**
 * Reusable application button component with consistent styling
 *
 * @example
 * ```tsx
 * <AppButton variant="primary" loading={isLoading}>
 *   Save
 * </AppButton>
 * ```
 */
export function AppButton({
  variant = 'primary',
  size = 'small',
  loading = false,
  startIcon,
  endIcon,
  fullWidth = false,
  disabled = false,
  children,
  sx,
  ...props
}: Readonly<AppButtonProps>) {
  // Size mappings
  const sizeStyles = {
    small: {
      py: 1,
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    medium: {
      py: 1.5,
      fontSize: '1rem',
      fontWeight: 600,
    },
    large: {
      py: 2,
      fontSize: '1.125rem',
      fontWeight: 600,
    },
  };

  // Variant styles
  const variantStyles = {
    primary: {
      backgroundColor: 'primary.main',
      color: 'white',
      '&:hover': {
        backgroundColor: 'primary.dark',
      },
      '&.Mui-disabled': {
        backgroundColor: 'primary.main',
        color: 'white',
        opacity: 0.6,
      },
    },
    secondary: {
      backgroundColor: 'secondary.main',
      color: 'text.primary',
      '&:hover': {
        backgroundColor: 'secondary.dark',
      },
      '&.Mui-disabled': {
        backgroundColor: 'secondary.main',
        color: 'text.primary',
        opacity: 0.6,
      },
    },
    outlined: {
      border: '1px solid',
      borderColor: 'grey.300',
      backgroundColor: 'transparent',
      color: 'text.secondary',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderColor: 'primary.main',
      },
      '&.Mui-disabled': {
        borderColor: 'grey.300',
        color: 'text.disabled',
        opacity: 1,
      },
    },
    text: {
      backgroundColor: 'transparent',
      color: 'primary.main',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
      '&.Mui-disabled': {
        color: 'text.disabled',
        opacity: 0.6,
      },
    },
  };

  // Determine MUI variant
  let muiVariant: 'text' | 'outlined' | 'contained' = 'contained';
  if (variant === 'outlined') {
    muiVariant = 'outlined';
  } else if (variant === 'text') {
    muiVariant = 'text';
  }

  return (
    <Button
      variant={muiVariant}
      fullWidth={fullWidth}
      disabled={loading || disabled}
      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : startIcon}
      endIcon={loading ? undefined : endIcon}
      sx={{
        ...sizeStyles[size],
        ...variantStyles[variant],
        textTransform: 'none',
        borderRadius: '10px',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}
